import { apiClient } from './client';
import type {
  AcceptQuoteDto,
  CreateQuoteRevisionDto,
  CreateNegotiationRoundDto,
  NegotiationRound,
  Quote,
  RespondNegotiationRoundDto,
} from '@/types/quote.types';

class QuoteService {
  async listQuotes(
    celebrationId: number,
    params?: { request_id?: number; status?: string },
  ): Promise<Quote[]> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/quotes`, { params });
    return response.data.data || [];
  }

  async getQuote(celebrationId: number, quoteId: number): Promise<Quote> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/quotes/${quoteId}`);
    return response.data.data;
  }

  async markViewed(celebrationId: number, quoteId: number): Promise<Quote> {
    const response = await apiClient.post(
      `/celebrations/${celebrationId}/quotes/${quoteId}/view`,
    );
    return response.data.data;
  }

  async acceptQuote(celebrationId: number, quoteId: number, payload?: AcceptQuoteDto): Promise<Quote> {
    const response = await apiClient.post(
      `/celebrations/${celebrationId}/quotes/${quoteId}/accept`,
      payload || {},
    );
    return response.data.data;
  }

  async rejectQuote(celebrationId: number, quoteId: number, reason?: string): Promise<Quote> {
    const response = await apiClient.post(
      `/celebrations/${celebrationId}/quotes/${quoteId}/reject`,
      { reason },
    );
    return response.data.data;
  }

  async withdrawQuote(celebrationId: number, quoteId: number): Promise<Quote> {
    const response = await apiClient.post(
      `/celebrations/${celebrationId}/quotes/${quoteId}/withdraw`,
    );
    return response.data.data;
  }

  async listNegotiationRounds(celebrationId: number, quoteId: number): Promise<NegotiationRound[]> {
    const response = await apiClient.get(
      `/celebrations/${celebrationId}/quotes/${quoteId}/negotiation-rounds`,
    );
    return response.data.data || [];
  }

  async createNegotiationRound(
    celebrationId: number,
    quoteId: number,
    payload?: CreateNegotiationRoundDto,
  ): Promise<NegotiationRound> {
    const response = await apiClient.post(
      `/celebrations/${celebrationId}/quotes/${quoteId}/negotiation-rounds`,
      payload || {},
    );
    return response.data.data;
  }

  async respondNegotiationRound(
    celebrationId: number,
    quoteId: number,
    roundId: number,
    payload: RespondNegotiationRoundDto,
  ): Promise<NegotiationRound> {
    const response = await apiClient.post(
      `/celebrations/${celebrationId}/quotes/${quoteId}/negotiation-rounds/${roundId}/respond`,
      payload,
    );
    return response.data.data;
  }

  async createRevision(
    celebrationId: number,
    quoteId: number,
    payload?: CreateQuoteRevisionDto,
  ): Promise<Quote> {
    const response = await apiClient.post(
      `/celebrations/${celebrationId}/quotes/${quoteId}/revisions`,
      payload || {},
    );
    return response.data.data;
  }
}

export const quoteService = new QuoteService();
